import { copyFor, urlWithLocale, type SupportedLocale } from './i18n';
import type { PhotoKeeperOffer } from './model';

type PurchaseRequest = { plan: 'personalLifetime' | 'familyLifetime'; binding: 'email' | 'code';
  purchaseConfirmed: true; appleId?: string; confirmAppleId?: string };
type Receipt = { id: string; secret: string; transactionId?: string; request?: PurchaseRequest };
type Purchase = { id: string; status: string; plan: string; binding: 'email' | 'code'; maskedAppleId?: string;
  checkoutUrl?: string; activationCode?: string; licenseStatus?: string };
const receiptPrefix = 'photokeeper.purchase.receipt.';
const activeKey = 'photokeeper.purchase.active';
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function websiteCopy(locale: SupportedLocale) {
  return locale === 'zh-CN' ? {
    choose: '选择许可，再选择绑定 iCloud 邮箱或领取激活码。', title: '选择激活方式',
    emailOption: '记录 iCloud 邮箱', emailDetail: '付款后保存激活码，在 PhotoKeeper 登录 Apple 账户并输入激活码。',
    codeOption: '暂不绑定，领取激活码', codeDetail: '付款确认后显示激活码。请复制或下载保存，再到 PhotoKeeper 中激活。',
    email: 'Apple 账户邮箱（用于登录 iCloud）', confirmEmail: '再次输入邮箱',
    emailHint: '可使用 Gmail、QQ 等 Apple 账户邮箱，不限于 @icloud.com。请填写登录账户，勿填收据邮箱或别名。',
    confirm: '我已核对激活方式和邮箱。邮箱由我自行确认，首次激活后账户绑定不可转移。',
    confirmCode: '我会妥善保存激活码，并确认首次激活后的账户绑定不可转移。',
    pay: '继续付款', close: '关闭', waiting: '正在准备购买，请稍候…', resume: '继续之前的购买',
    cancel: '取消未付款订单，重新选择', pending: '正在确认付款，请稍后再次检查。请保留此页面。',
    unavailable: '无法继续这笔购买。请重试；若该账户已有订单，请登录 PhotoKeeper 或联系 support@photokeeper.app。',
    notOpen: '购买暂未开放，支付服务正在开通中。请稍后再试。',
    limited: '操作过于频繁，请稍后重试。', mismatch: '两次填写的邮箱不一致。',
    storage: '请允许此网站使用浏览器存储，以便付款后领取购买凭证。',
    success: '购买已确认', bound: '在 PhotoKeeper 登录 Apple 账户并输入下方激活码。请保存激活码以便在新设备恢复授权。',
    codeLabel: 'PhotoKeeper 激活码', copy: '复制激活码', copied: '已复制', download: '下载激活码',
    codeInstructions: '请妥善保存激活码。在 PhotoKeeper 登录 Apple 账户后，在许可设置中展开“使用已有许可”，输入激活码完成激活。首次绑定的账号为主账号，只有主账号可以申请退款。',
    check: '重新检查付款状态', missing: '当前浏览器没有这笔购买的领取凭证。请返回付款时的标签页，或凭订单联系支持。',
    inactive: '这笔授权已退款、停用或激活码已更换。请在 PhotoKeeper 查看授权状态，或联系支持。',
    expired: '此订单已关闭，可返回购买页重新选择。',
  } : {
    choose: 'Choose a license, then link your iCloud email or receive an activation code.', title: 'Choose how to activate',
    emailOption: 'Record my iCloud email', emailDetail: 'After payment, save your activation code and enter it in PhotoKeeper after signing in.',
    codeOption: 'Get an activation code instead', codeDetail: 'Your code appears after payment is confirmed. Copy or download it, then activate in PhotoKeeper.',
    email: 'Apple Account email (used to sign in to iCloud)', confirmEmail: 'Enter the email again',
    emailHint: 'Any Apple Account email is supported, including Gmail. Enter your sign-in email, not a receipt email or alias.',
    confirm: 'I have checked my choice and email. I confirm the email myself; account bindings cannot be transferred after activation.',
    confirmCode: 'I will keep my activation code safe. Account bindings cannot be transferred after activation.',
    pay: 'Continue to payment', close: 'Close', waiting: 'Preparing your purchase…', resume: 'Resume your previous purchase',
    cancel: 'Cancel the unpaid order and start again', pending: 'Confirming your payment. Keep this page open and check again shortly.',
    unavailable: 'This purchase cannot continue. Try again, or sign in to PhotoKeeper if you already purchased. Contact support@photokeeper.app for help.',
    notOpen: 'Purchases are not open yet. Payment setup is still in progress. Please check back later.',
    limited: 'Too many attempts. Please try again later.', mismatch: 'The two email addresses do not match.',
    storage: 'Allow browser storage for this site so you can receive your purchase after payment.',
    success: 'Purchase confirmed', bound: 'Sign in to PhotoKeeper and enter the code below. Keep it to restore your license on another device.',
    codeLabel: 'PhotoKeeper activation code', copy: 'Copy activation code', copied: 'Copied', download: 'Download activation code',
    codeInstructions: 'Keep this code safe. Sign in to your Apple Account in PhotoKeeper, open “Activate an existing License” in License settings, and enter the activation / recovery code. The first bound account is the primary account and is the only account that can request refunds.',
    check: 'Check payment status', missing: 'This browser does not have the receipt credential. Return to the tab used for payment or contact support with your order.',
    inactive: 'This license is refunded or inactive, or its code has been replaced. Check PhotoKeeper or contact support.',
    expired: 'This order is closed. Return to the purchase page to start again.',
  };
}

export function parseReceipt(value: unknown): Receipt | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const receipt = value as Partial<Receipt>;
  if (typeof receipt.id !== 'string' || !uuidPattern.test(receipt.id) || typeof receipt.secret !== 'string' ||
      !/^[A-Za-z0-9_-]{43}$/.test(receipt.secret) || (receipt.transactionId !== undefined &&
      !/^txn_[a-z\d]{26}$/.test(receipt.transactionId))) return undefined;
  const request = receipt.request;
  if (request && (!['personalLifetime', 'familyLifetime'].includes(request.plan) ||
      !['email', 'code'].includes(request.binding) || request.purchaseConfirmed !== true ||
      (request.binding === 'email' && (typeof request.appleId !== 'string' || request.appleId.length > 320 ||
        typeof request.confirmAppleId !== 'string' || request.confirmAppleId.length > 320)))) return undefined;
  return { id: receipt.id, secret: receipt.secret, ...(receipt.transactionId ? { transactionId: receipt.transactionId } : {}),
    ...(request ? { request: { plan: request.plan, binding: request.binding, purchaseConfirmed: true,
      ...(request.binding === 'email' ? { appleId: request.appleId, confirmAppleId: request.confirmAppleId } : {}) } } : {}) };
}

export function validatedCheckoutUrl(value: string, origin: string): URL {
  const url = new URL(value);
  if (url.origin !== origin || url.username || url.password || url.pathname !== '/checkout/' ||
      url.searchParams.getAll('_ptxn').length !== 1 || !/^txn_[a-z\d]{26}$/.test(url.searchParams.get('_ptxn') ?? '')) {
    throw new Error('invalid_checkout_url');
  }
  return url;
}

export function purchaseSuccessUrl(transactionId: string, locale: SupportedLocale): string {
  const url = urlWithLocale(new URL('/welcome', window.location.origin), locale);
  const receipt = readActiveReceipt();
  if (receipt?.transactionId === transactionId) url.searchParams.set('purchase', receipt.id);
  return url.toString();
}

export function openWebsitePurchase(offer: PhotoKeeperOffer, locale: SupportedLocale): void {
  const copy = websiteCopy(locale);
  const dialog = document.createElement('dialog');
  dialog.className = 'purchase-dialog';
  const heading = text('h2', copy.title);
  heading.id = 'purchase-dialog-title';
  dialog.setAttribute('aria-labelledby', heading.id);
  const close = button(copy.close, () => dialog.close());
  close.className = 'dialog-close';
  dialog.append(close, heading, text('p', copyFor(locale).offers[offer.id].name));
  const status = text('p', '');
  status.className = 'purchase-status';
  status.setAttribute('role', 'status');
  const form = document.createElement('form');
  const choices = document.createElement('fieldset');
  choices.append(text('legend', copy.title));
  const emailMode = radio('email', copy.emailOption, copy.emailDetail, true);
  const codeMode = radio('code', copy.codeOption, copy.codeDetail, false);
  choices.append(emailMode.label, codeMode.label);
  const emails = document.createElement('div');
  const email = emailField('purchase-email', copy.email);
  const confirmation = emailField('purchase-email-confirm', copy.confirmEmail);
  emails.append(email.label, confirmation.label, text('p', copy.emailHint));
  emails.className = 'purchase-email-fields';
  const confirm = document.createElement('input');
  confirm.type = 'checkbox'; confirm.required = true;
  const confirmLabel = document.createElement('label');
  confirmLabel.className = 'purchase-confirm';
  const confirmationText = text('span', copy.confirm);
  confirmLabel.append(confirm, confirmationText);
  const submit = button(copy.pay); submit.type = 'submit'; submit.className = 'primary-button';
  form.append(choices, emails, confirmLabel, submit);
  const modesChanged = () => {
    emails.hidden = !emailMode.input.checked;
    email.input.required = confirmation.input.required = emailMode.input.checked;
    confirmationText.textContent = emailMode.input.checked ? copy.confirm : copy.confirmCode;
  };
  choices.addEventListener('change', modesChanged); modesChanged();
  let active = readActiveReceipt();
  const pending = document.createElement('div');
  const resume = button(copy.resume, () => void run(async () => followPurchase(await purchaseRequest(active!), active!)));
  resume.className = 'primary-button';
  const cancel = button(copy.cancel, () => void run(async () => {
    const purchase = await purchaseRequest(active!, 'DELETE');
    if (purchase.status === 'completed') return followPurchase(purchase, active!);
    clearActive(); active = undefined; form.hidden = false; pending.hidden = true; status.textContent = '';
  }));
  pending.append(resume, cancel);
  pending.className = 'purchase-resume'; pending.hidden = !active; form.hidden = Boolean(active);
  dialog.append(pending, form, status);
  dialog.addEventListener('close', () => dialog.remove(), { once: true });
  document.body.append(dialog); dialog.showModal();

  async function run(action: () => Promise<void>) {
    submit.disabled = resume.disabled = cancel.disabled = true;
    status.textContent = copy.waiting;
    try { await action(); } catch (error) {
      status.textContent = error instanceof PurchaseError && error.checkoutClosed ? copy.notOpen :
        error instanceof PurchaseError && error.status === 429 ? copy.limited :
        error instanceof Error && error.message === 'storage_unavailable' ? copy.storage : copy.unavailable;
    } finally { submit.disabled = resume.disabled = cancel.disabled = false; }
  }
  function followPurchase(purchase: Purchase, receipt: Receipt) {
    if (purchase.status === 'completed') {
      const url = urlWithLocale(new URL('/welcome', window.location.origin), locale);
      url.searchParams.set('purchase', receipt.id); window.location.assign(url.toString()); return;
    }
    if (purchase.status !== 'pending') {
      clearActive(); active = undefined; pending.hidden = true; form.hidden = false; status.textContent = copy.expired; return;
    }
    if (!purchase.checkoutUrl) { status.textContent = copy.pending; return; }
    const url = validatedCheckoutUrl(purchase.checkoutUrl, window.location.origin);
    receipt.transactionId = url.searchParams.get('_ptxn')!;
    delete receipt.request;
    saveReceipt(receipt);
    window.location.assign(urlWithLocale(url, locale).toString());
  }
  form.addEventListener('submit', event => {
    event.preventDefault();
    const normalize = (value: string) => value.trim().normalize('NFKC').toLowerCase();
    if (emailMode.input.checked && normalize(email.input.value) !== normalize(confirmation.input.value)) {
      status.textContent = copy.mismatch; confirmation.input.focus(); return;
    }
    void run(async () => {
      const receipt: Receipt = { id: crypto.randomUUID(), secret: randomSecret(), request: {
        plan: offer.id === 'personal' ? 'personalLifetime' : 'familyLifetime',
        binding: emailMode.input.checked ? 'email' : 'code', purchaseConfirmed: true,
        ...(emailMode.input.checked ? { appleId: email.input.value, confirmAppleId: confirmation.input.value } : {}) } };
      if (!confirm.checked) return;
      saveReceipt(receipt);
      active = receipt; form.hidden = true; pending.hidden = false;
      const response = await submitPurchase(receipt);
      if (!response.ok) {
        const failure = await PurchaseError.fromResponse(response);
        // Validation and conflict responses did not create an order. Uncertain errors retain the receipt.
        if ((response.status >= 400 && response.status < 500) || failure.checkoutClosed) {
          clearActive(); active = undefined; form.hidden = false; pending.hidden = true;
          try { sessionStorage.removeItem(receiptPrefix + receipt.id); } catch { /* No payable order exists. */ }
        }
        else { active = receipt; form.hidden = true; pending.hidden = false; }
        throw failure;
      }
      active = receipt; form.hidden = true; pending.hidden = false;
      followPurchase(await response.json() as Purchase, receipt);
    });
  });
}

export function renderWebsiteReceipt(panel: HTMLElement, locale: SupportedLocale): void {
  const id = new URL(window.location.href).searchParams.get('purchase');
  if (!id) return;
  const copy = websiteCopy(locale);
  panel.replaceChildren(text('h1', copy.check));
  const status = text('p', copy.pending); status.setAttribute('role', 'status');
  panel.append(status);
  const receipt = readReceipt(id);
  if (!receipt) { status.textContent = copy.missing; return; }
  let attempts = 0;
  let loading = false;
  const retry = button(copy.check, () => { attempts = 0; void update(); });
  panel.append(retry);
  async function update() {
    if (loading || !panel.isConnected) return;
    loading = true; retry.disabled = true;
    try {
      const purchase = await purchaseRequest(receipt!);
      if (purchase.status === 'completed') {
        clearActive();
        delete receipt!.request;
        try { sessionStorage.setItem(receiptPrefix + receipt!.id, JSON.stringify(receipt)); } catch { /* Already paid. */ }
        panel.replaceChildren(text('h1', copy.success));
        if (purchase.licenseStatus !== 'active') { panel.append(text('p', copy.inactive)); return; }
        if (purchase.binding === 'email') {
          panel.append(text('p', purchase.maskedAppleId ?? ''), text('p', copy.bound));
        }
        if (!purchase.activationCode || !/^PK1-[A-Za-z0-9_-]{22}$/.test(purchase.activationCode)) {
          panel.append(text('p', copy.inactive)); return;
        }
        const code = purchase.activationCode;
        const label = text('label', copy.codeLabel);
        const input = document.createElement('input'); input.value = code; input.readOnly = true;
        input.className = 'activation-code'; label.append(input);
        const copyButton = button(copy.copy, () => void navigator.clipboard.writeText(code)
          .then(() => { copyButton.textContent = copy.copied; }).catch(() => { input.select(); }));
        const download = button(copy.download, () => {
          const url = URL.createObjectURL(new Blob([`PhotoKeeper\n${copy.codeLabel}: ${code}\n\n${copy.codeInstructions}\n`],
            { type: 'text/plain;charset=utf-8' }));
          const link = document.createElement('a'); link.href = url; link.download = 'PhotoKeeper-activation-code.txt';
          link.click(); setTimeout(() => URL.revokeObjectURL(url), 1_000);
        });
        panel.append(label, text('p', copy.codeInstructions), copyButton, download); return;
      }
      status.textContent = purchase.status === 'pending' ? copy.pending : copy.expired;
      if (purchase.status === 'pending' && ++attempts < 20) setTimeout(() => void update(), 3_000);
    } catch { status.textContent = copy.missing; }
    finally { loading = false; retry.disabled = false; }
  }
  void update();
}

async function purchaseRequest(receipt: Receipt, method = 'GET'): Promise<Purchase> {
  const response = await fetch(`/v1/website-purchases/${receipt.id}`, { method,
    headers: { authorization: `Bearer ${receipt.secret}`, accept: 'application/json' },
    cache: 'no-store', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer' });
  if (response.status === 404 && receipt.request) {
    // Retry the same id after a lost request; never invent another order to recover a network failure.
    const recovered = await submitPurchase(receipt);
    if (!recovered.ok) throw await PurchaseError.fromResponse(recovered);
    if (method === 'GET') return recovered.json() as Promise<Purchase>;
    return purchaseRequest({ ...receipt, request: undefined }, method);
  }
  if (!response.ok) throw await PurchaseError.fromResponse(response);
  return response.json() as Promise<Purchase>;
}

function submitPurchase(receipt: Receipt): Promise<Response> {
  return fetch('/v1/website-purchases', { method: 'POST', credentials: 'omit',
    headers: { 'content-type': 'application/json', accept: 'application/json' }, cache: 'no-store',
    redirect: 'error', referrerPolicy: 'no-referrer',
    body: JSON.stringify({ id: receipt.id, secret: receipt.secret, ...receipt.request }) });
}

function readReceipt(id: string): Receipt | undefined {
  try { if (uuidPattern.test(id)) return parseReceipt(JSON.parse(sessionStorage.getItem(receiptPrefix + id) ?? 'null')); }
  catch { /* A receipt is never reconstructed from untrusted query parameters. */ }
  return undefined;
}
function readActiveReceipt(): Receipt | undefined {
  try { return readReceipt(sessionStorage.getItem(activeKey) ?? ''); } catch { return undefined; }
}
function saveReceipt(receipt: Receipt): void {
  try {
    sessionStorage.setItem(receiptPrefix + receipt.id, JSON.stringify(receipt));
    sessionStorage.setItem(activeKey, receipt.id);
    if (!readReceipt(receipt.id)) throw new Error();
  } catch { throw new Error('storage_unavailable'); }
}
function clearActive(): void { try { sessionStorage.removeItem(activeKey); } catch { /* Receipt remains readable. */ } }
function randomSecret(): string {
  return btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
function text<K extends keyof HTMLElementTagNameMap>(tag: K, value: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag); element.textContent = value; return element;
}
function button(label: string, action?: () => void): HTMLButtonElement {
  const element = text('button', label); element.type = 'button';
  if (action) element.addEventListener('click', action); return element;
}
function radio(value: string, title: string, detail: string, checked: boolean) {
  const label = document.createElement('label'); label.className = 'purchase-option';
  const input = document.createElement('input'); input.type = 'radio'; input.name = 'binding';
  input.value = value; input.checked = checked;
  const description = document.createElement('span'); description.append(text('strong', title), text('small', detail));
  label.append(input, description); return { label, input };
}
function emailField(id: string, title: string) {
  const label = text('label', title); label.htmlFor = id;
  const input = document.createElement('input'); input.type = 'email'; input.id = id; input.maxLength = 320;
  input.autocomplete = 'off'; input.autocapitalize = 'none'; input.spellcheck = false;
  label.append(input); return { label, input };
}
class PurchaseError extends Error {
  constructor(readonly status: number, readonly code?: string) { super('purchase_request_failed'); }
  get checkoutClosed(): boolean { return this.code === 'checkout_not_enabled' || this.code === 'checkout_domain_unapproved'; }
  static async fromResponse(response: Response): Promise<PurchaseError> {
    const payload = await response.json().catch(() => null);
    const code = typeof payload?.error?.code === 'string' && /^[a-z_]{1,100}$/.test(payload.error.code)
      ? payload.error.code : undefined;
    return new PurchaseError(response.status, code);
  }
}
